import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { captureEvent, tierLabel } from "../analytics";
import { postCheckout } from "../api/client";
import { usePollUserTier } from "../utils/usePollUserTier";
import { useAuth } from "../auth/useAuth";
import { NavBar } from "../components/NavBar";

/**
 * UpgradePage — Task 9.3. Feature comparison + "Subscribe" → Stripe
 * Checkout. On return from Stripe with ?upgrade=success, Pattern C polling
 * (Task 6.12) watches /user/tier until the webhook flips the tier to paid,
 * then refreshes the session and routes home.
 */

// Mirrors PLAN_FEATURES in the marketing repo (pricetilt-marketing
// src/config/pricing.ts) — keep the two lists in sync when pricing or
// feature framing changes.
const FEATURES: { label: string; free: string | null; pro: string }[] = [
  { label: "AI buying-agent summary on any address", free: "✓ (unlimited)", pro: "✓ (unlimited)" },
  { label: "Negotiation framing & due-diligence guidance", free: "✓", pro: "✓" },
  { label: "Public / MLS facts", free: "✓", pro: "✓" },
  { label: "Verified Tilt Map, exact negotiation position + AI offer range", free: null, pro: "✓" },
  { label: "Owner & equity profile, balance, equity, tenure, motivation signals", free: null, pro: "✓" },
  { label: "Verified fair value & comps, licensed valuation + comparable sales", free: null, pro: "✓" },
  { label: "Permit & renovation history", free: null, pro: "✓" },
  { label: "Flood & insurance risk, FEMA zone + cost impact", free: null, pro: "✓" },
  { label: "Saved properties, comparison & price-drop alerts", free: null, pro: "✓" },
  { label: "Full AI agent, memory across sessions", free: null, pro: "✓" },
  { label: "Verified reports per month", free: "1 (free Pro report)", pro: "15" },
];

export function UpgradePage() {
  const auth = useAuth();
  const params = new URLSearchParams(window.location.search);
  const returning = params.get("upgrade") === "success";
  // Whether the user has a free-tier report to return to (persisted by App
  // in sessionStorage under "pt.propertyContext"). Drives the back link so
  // it doesn't show for users who hit /upgrade directly.
  let hasReport = false;
  try {
    hasReport = !!sessionStorage.getItem("pt.propertyContext");
  } catch {
    hasReport = false;
  }
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  // Pattern C — after Stripe returns, poll until the tier flips to paid.
  const poll = usePollUserTier({ active: returning && auth.tier !== "paid" });

  useEffect(() => {
    if (poll.tier === "paid") {
      // Flag the just-upgraded transition so App can carry the preserved
      // free-tier property straight into a Pro score (Pattern C / Req 20.5),
      // instead of dropping the new subscriber on a blank search form.
      try {
        sessionStorage.setItem("pt.justUpgraded", "1");
      } catch {
        /* sessionStorage unavailable — App just shows the normal form */
      }
      void auth.refresh(true).then(() => {
        window.location.href = "/";
      });
    }
  }, [poll.tier, auth]);

  async function handleSubscribe() {
    setError(null);
    setRedirecting(true);
    // Before the await — the Stripe redirect replaces the page and would
    // race a capture queued after it.
    captureEvent("checkout_started", { tier: tierLabel(auth.tier) });
    try {
      const { checkout_url } = await postCheckout();
      window.location.href = checkout_url;
    } catch (e) {
      setError((e as { message?: string }).message || "Could not start checkout.");
      setRedirecting(false);
    }
  }

  if (auth.tier === "paid" && !returning) {
    return (
      <div className="app">
        <NavBar />
        <main>
          <h1>You&rsquo;re on PriceTilt Pro</h1>
          <p>You already have full access. Head back to score a property.</p>
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <NavBar />
      <main>
        <header className="page-header">
          {hasReport && (
            <Link to="/" className="back-link">
              ← Back to PriceTilt Report
            </Link>
          )}
          <h1>Upgrade to PriceTilt Pro</h1>
          <p className="upgrade-price" aria-label="PriceTilt Pro pricing">
            <span className="upgrade-price-amount">$29</span>
            <span className="upgrade-price-period">/mo</span>
            <span className="upgrade-price-allotment">
              15 verified reports / month
            </span>
          </p>
        </header>

        {returning && auth.tier !== "paid" && (
          <p className="upgrade-polling" role="status">
            Confirming your upgrade… this can take a few seconds.
            {poll.error && <span className="error"> {poll.error.message}</span>}
          </p>
        )}

        <table className="upgrade-table">
          <thead>
            <tr>
              <th>Feature</th>
              <th>Free</th>
              <th>Pro</th>
            </tr>
          </thead>
          <tbody>
            {FEATURES.map((f) => (
              <tr key={f.label}>
                <td>{f.label}</td>
                <td>{f.free ?? "—"}</td>
                <td>{f.pro}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {error && <p className="error" role="alert">{error}</p>}

        <button
          type="button"
          className="submit upgrade-subscribe"
          onClick={() => void handleSubscribe()}
          disabled={redirecting}
        >
          {redirecting ? "Starting checkout…" : "Subscribe to Pro"}
        </button>

        <p className="upgrade-cancel-note">
          Cancel anytime, no auto-renew traps, no cancellation runaround.
          Subscribe while you&rsquo;re shopping, cancel the moment you close.
        </p>
      </main>
    </div>
  );
}
